const PINK_DOLPHIN = Object.freeze({
  kind: "item",
  itemId: 820986,
  label: "Pink Dolphin",
});

const SEA_BUNNY = Object.freeze({
  kind: "item",
  itemId: 821025,
  label: "Sea Bunny",
});

const QUERY_ICON = Object.freeze({
  kind: "icon",
  icon: "magnifying-glass",
  label: "Query",
});

const FOCUS_QUERY_ICON = Object.freeze({
  kind: "icon",
  icon: "target-fill",
  label: "Focus current query",
});

const LAYERS_ICON = Object.freeze({
  kind: "icon",
  icon: "squares-2x2",
  label: "Layers",
});

const HOTSPOTS_ICON = Object.freeze({
  kind: "icon",
  icon: "bird-fill",
  label: "Hotspots",
});

const DETAILS_ICON = Object.freeze({
  kind: "icon",
  icon: "inspect-fill",
  label: "Details",
});

const ZONE_TAB_ICON = Object.freeze({
  kind: "icon",
  icon: "hover-zone",
  label: "Zone",
});

const HOTSPOT_TAB_ICON = Object.freeze({
  kind: "icon",
  icon: "fish-fill",
  label: "Hotspot",
});

const REGIONS_ICON = Object.freeze({
  kind: "icon",
  icon: "trade-origin",
  label: "Regions",
});

const REGION_GROUPS_ICON = Object.freeze({
  kind: "icon",
  icon: "hover-resources",
  label: "Region Groups",
});

const ORIGIN_ICON = Object.freeze({
  kind: "icon",
  icon: "trade-origin",
  label: "Origin",
});

const RESOURCES_ICON = Object.freeze({
  kind: "icon",
  icon: "hover-resources",
  label: "Resources",
});

const TERRITORY_TAB_ICON = Object.freeze({
  kind: "icon",
  icon: "hover-resources",
  label: "Territory",
});

const ZONE_MASK_ICON = Object.freeze({
  kind: "icon",
  icon: "hover-zone",
  label: "Zone Mask",
});

const FISH_EVIDENCE_ICON = Object.freeze({
  kind: "icon",
  icon: "fish-fill",
  label: "Fish Evidence",
});

const GROUP_SHARE = Object.freeze({
  kind: "icon",
  icon: "percentage-fill",
  label: "group share",
});

const BASE_PRICE = Object.freeze({
  kind: "icon",
  icon: "coin-2-fill",
  label: "base price",
});

const WITHIN_GROUP_RATE = Object.freeze({
  kind: "icon",
  icon: "percentage-fill",
  label: "within-group rate",
});

const PRESENCE_PROVENANCE = Object.freeze({
  kind: "provenance",
  label: "presence provenance",
  tone: "database",
});

const RATE_PROVENANCE = Object.freeze({
  kind: "provenance",
  label: "rate provenance",
  tone: "rate-known",
});

const SAME_FISH_QUERY = Object.freeze({
  kind: "code",
  label: "red AND missing",
});

const EXPLICIT_SAME_FISH_QUERY = Object.freeze({
  kind: "code",
  label: "[red AND missing]",
});

const SEPARATE_FISH_QUERY = Object.freeze({
  kind: "code",
  label: "[red] AND [missing]",
});

function richCopy(message, tokens = {}) {
  return Object.freeze({
    message: Object.freeze(message),
    tokens: Object.freeze({ ...tokens }),
  });
}

function step(title, body, action, options = {}) {
  return Object.freeze({
    title: Object.freeze(title),
    body,
    action,
    ...options,
  });
}

function tutorial(level, title, steps) {
  return Object.freeze({
    level,
    title: Object.freeze(title),
    steps: Object.freeze(steps),
  });
}

export const MAP_TUTORIALS = Object.freeze({
  "query-basics": tutorial(
    "beginner",
    ["tutorial.query_basics.title", "Use the query system"],
    [
      step(
        ["tutorial.query_basics.step1.title", "Find Pink Dolphin"],
        richCopy(
          [
            "tutorial.query_basics.step1.body",
            "Type “Pink” in {$query}. Stop when it resolves to {$pink_dolphin} before you finish the name.",
          ],
          { query: QUERY_ICON, pink_dolphin: PINK_DOLPHIN },
        ),
        "apply-pink-dolphin",
        { coachAnchor: "query-surface", coachPlacement: "left-attached" },
      ),
      step(
        ["tutorial.query_basics.step2.title", "Confirm Pink Dolphin"],
        richCopy(
          [
            "tutorial.query_basics.step2.body",
            "The highlighted query term is {$pink_dolphin}. Finishing the full name also continues automatically.",
          ],
          { pink_dolphin: PINK_DOLPHIN },
        ),
        "confirm-pink-dolphin",
        {
          allowProceed: true,
          coachAnchor: "query-surface",
          coachPlacement: "left-attached",
        },
      ),
      step(
        ["tutorial.query_basics.step3.title", "Clear the query"],
        richCopy(
          [
            "tutorial.query_basics.step3.body",
            "Clear {$pink_dolphin} with the clear button, or erase the text in {$query}.",
          ],
          { pink_dolphin: PINK_DOLPHIN, query: QUERY_ICON },
        ),
        "clear-query",
        { coachAnchor: "query-surface", coachPlacement: "left-attached" },
      ),
      step(
        ["tutorial.query_basics.step4.title", "Find Sea Bunny"],
        richCopy(
          [
            "tutorial.query_basics.step4.body",
            "Search for {$sea_bunny} and apply the matching result. The map now shows its fishing evidence.",
          ],
          { sea_bunny: SEA_BUNNY },
        ),
        "apply-sea-bunny",
        { coachAnchor: "query-surface", coachPlacement: "left-attached" },
      ),
      step(
        ["tutorial.query_basics.step5.title", "Bring the results into view"],
        richCopy(
          [
            "tutorial.query_basics.step5.body",
            "Optionally pan or zoom away from the results. Use {$focus_query} in Query to bring the Sea Bunny matches back into view.",
          ],
          { focus_query: FOCUS_QUERY_ICON },
        ),
        "focus-query",
        { coachAnchor: "query-surface", coachPlacement: "left-attached" },
      ),
      step(
        ["tutorial.query_basics.step6.title", "Load a catch profile"],
        richCopy(
          [
            "tutorial.query_basics.step6.body",
            "Click open water in any fishing zone. Wait for {$details} to show its Catch Profile; the zone does not need Sea Bunny.",
          ],
          { details: DETAILS_ICON },
        ),
        "select-map-point",
        { coachAnchor: "query-surface", coachPlacement: "left-attached" },
      ),
      step(
        ["tutorial.query_basics.step7.title", "Catch groups"],
        richCopy(
          [
            "tutorial.query_basics.step7.body",
            "Select {$zone_tab} in {$details} if needed. Catch groups collect fish from the same loot category, such as Prize or Rare. {$group_share} is this group's portion of the whole profile.",
          ],
          {
            zone_tab: ZONE_TAB_ICON,
            details: DETAILS_ICON,
            group_share: GROUP_SHARE,
          },
        ),
        "inspect-catch-group",
        { allowProceed: true },
      ),
      step(
        ["tutorial.query_basics.step8.title", "Fish and base price"],
        richCopy(
          [
            "tutorial.query_basics.step8.body",
            "The grade-colored icon and name identify the fish; {$base_price} is its vendor base price.",
          ],
          { base_price: BASE_PRICE },
        ),
        "inspect-catch-item",
        { allowProceed: true },
      ),
      step(
        ["tutorial.query_basics.step9.title", "Within-group rate"],
        richCopy(
          [
            "tutorial.query_basics.step9.body",
            "{$within_group_rate} is the fish's share within this group, not the whole profile, and may display as a percentage or weight.",
          ],
          { within_group_rate: WITHIN_GROUP_RATE },
        ),
        "inspect-catch-rate",
        { allowProceed: true },
      ),
    ],
  ),
  "window-basics": tutorial(
    "beginner",
    ["tutorial.window_basics.title", "Use the window system"],
    [
      step(
        ["tutorial.window_basics.step1.title", "Hide and recover this tutorial"],
        richCopy([
          "tutorial.window_basics.step1.body",
          "Choose “Hide tutorial” below. Reopen Help & Tutorials with the question-mark button, then choose Continue here.",
        ]),
        "hide-and-resume-tutorial",
      ),
      step(
        ["tutorial.window_basics.step2.title", "Close and reopen Query"],
        richCopy(
          [
            "tutorial.window_basics.step2.body",
            "Use the toolbar’s {$query} button to close the Query window, then use the same button to open it again.",
          ],
          { query: QUERY_ICON },
        ),
        "cycle-query-window",
        {
          phaseBodies: Object.freeze({
            "needs-initial-open": richCopy(
              [
                "tutorial.window_basics.step2.open_first",
                "{$query} is already closed. Open it first; then close and reopen it to complete the full cycle.",
              ],
              { query: QUERY_ICON },
            ),
            "needs-reopen": richCopy(
              [
                "tutorial.window_basics.step2.reopen",
                "{$query} is closed. Use the same toolbar button to reopen it.",
              ],
              { query: QUERY_ICON },
            ),
          }),
        },
      ),
      step(
        ["tutorial.window_basics.step3.title", "Collapse and expand Layers"],
        richCopy(
          [
            "tutorial.window_basics.step3.body",
            "Click the {$layers} title bar to collapse the window, then click the title bar again to expand it.",
          ],
          { layers: LAYERS_ICON },
        ),
        "cycle-layer-collapse",
        {
          phaseBodies: Object.freeze({
            "needs-initial-expand": richCopy(
              [
                "tutorial.window_basics.step3.expand_first",
                "{$layers} is already collapsed. Expand it first; then collapse and expand it once more.",
              ],
              { layers: LAYERS_ICON },
            ),
            "needs-expand": richCopy(
              [
                "tutorial.window_basics.step3.expand",
                "{$layers} is collapsed. Click its title bar again to expand it.",
              ],
              { layers: LAYERS_ICON },
            ),
          }),
        },
      ),
      step(
        ["tutorial.window_basics.step4.title", "Move a window"],
        richCopy(
          [
            "tutorial.window_basics.step4.body",
            "Drag the {$layers} title bar to move the window.",
          ],
          { layers: LAYERS_ICON },
        ),
        "move-layer-window",
      ),
    ],
  ),
  "layer-basics": tutorial(
    "beginner",
    ["tutorial.layer_basics.title", "Use the layer system"],
    [
      step(
        ["tutorial.layer_basics.step1.title", "Show Hotspots"],
        richCopy(
          [
            "tutorial.layer_basics.step1.body",
            "Open {$layers} if needed, then turn on {$hotspots}.",
          ],
          { layers: LAYERS_ICON, hotspots: HOTSPOTS_ICON },
        ),
        "show-hotspots-layer",
        {
          resumeBody: richCopy(
            [
              "tutorial.layer_basics.step1.resume",
              "{$hotspots} are hidden. Turn the layer back on to finish the comparison.",
            ],
            { hotspots: HOTSPOTS_ICON },
          ),
          filteredBody: richCopy(
            [
              "tutorial.layer_basics.step1.query_filtered",
              "{$hotspots} is on, but the current {$query} hides every hotspot. Clear the query to show them.",
            ],
            { hotspots: HOTSPOTS_ICON, query: QUERY_ICON },
          ),
        },
      ),
      step(
        ["tutorial.layer_basics.step2.title", "Inspect a hotspot"],
        richCopy(
          [
            "tutorial.layer_basics.step2.body",
            "Select any visible {$hotspots} marker on the map. Details should identify the selected landmark as a hotspot.",
          ],
          { hotspots: HOTSPOTS_ICON },
        ),
        "inspect-hotspot",
        {
          rejectedSelectionBody: richCopy(
            [
              "tutorial.layer_basics.step2.not_hotspot",
              "That is a different landmark. Select a visible {$hotspots} marker instead.",
            ],
            { hotspots: HOTSPOTS_ICON },
          ),
        },
      ),
      step(
        ["tutorial.layer_basics.step3.title", "Open the Hotspot details"],
        richCopy(
          [
            "tutorial.layer_basics.step3.body",
            "Choose {$hotspot_tab} in {$details}. Review its catch counts, bite timing, lifetime, loot, and model previews.",
          ],
          { details: DETAILS_ICON, hotspot_tab: HOTSPOT_TAB_ICON },
        ),
        "select-hotspot-tab",
        { allowProceed: true },
      ),
      step(
        ["tutorial.layer_basics.step4.title", "Hide Hotspots again"],
        richCopy(
          [
            "tutorial.layer_basics.step4.body",
            "Turn {$hotspots} off after reviewing {$hotspot_tab}. The layer is useful on demand but visually crowded.",
          ],
          { hotspot_tab: HOTSPOT_TAB_ICON, hotspots: HOTSPOTS_ICON },
        ),
        "hide-hotspots-layer",
      ),
    ],
  ),
  bookmarks: tutorial(
    "intermediate",
    ["tutorial.bookmarks.title", "Save and exchange bookmarks"],
    [
      step(
        ["tutorial.bookmarks.step1.title", "Place a bookmark"],
        richCopy([
          "tutorial.bookmarks.step1.body",
          "Open Bookmarks, choose New bookmark, then select the fishing location you want to keep.",
        ]),
        "place-bookmark",
      ),
      step(
        ["tutorial.bookmarks.step2.title", "Return to a bookmark"],
        richCopy([
          "tutorial.bookmarks.step2.body",
          "Activate the bookmark you just created to return to its world point and inspect the same Details facts.",
        ]),
        "activate-bookmark",
      ),
      step(
        ["tutorial.bookmarks.step3.title", "Copy coordinates from Details"],
        richCopy(
          [
            "tutorial.bookmarks.step3.body",
            "Select {$territory_tab} in {$details} to see the current landmark's world coordinates. Then click the landmark-type button in the Details title bar (for example, Bookmark) to copy those coordinates as bookmark XML.",
          ],
          {
            territory_tab: TERRITORY_TAB_ICON,
            details: DETAILS_ICON,
          },
        ),
        "copy-details-bookmark",
      ),
      step(
        ["tutorial.bookmarks.step4.title", "Copy or export bookmark XML"],
        richCopy(
          [
            "tutorial.bookmarks.step4.body",
            "Select a bookmark, then use Copy or Export. To add it to Black Desert, close the game, back up {$game_variables}, replace its {$worldmap_bookmark} block with the exported XML, and restart.",
          ],
          {
            game_variables: Object.freeze({
              kind: "code",
              label: "GameVariables.xml",
            }),
            worldmap_bookmark: Object.freeze({
              kind: "code",
              label: "<WorldmapBookMark>",
            }),
          },
        ),
        "export-bookmarks",
      ),
      step(
        ["tutorial.bookmarks.step5.title", "Import bookmarks from the game"],
        richCopy(
          [
            "tutorial.bookmarks.step5.body",
            "To bring local game bookmarks to the site, copy the {$worldmap_bookmark} block from {$game_variables} and choose Paste, or save that block as XML and choose Import.",
          ],
          {
            game_variables: Object.freeze({
              kind: "code",
              label: "GameVariables.xml",
            }),
            worldmap_bookmark: Object.freeze({
              kind: "code",
              label: "<WorldmapBookMark>",
            }),
          },
        ),
        "import-bookmarks",
      ),
    ],
  ),
  "layer-settings": tutorial(
    "intermediate",
    ["tutorial.layer_settings.title", "Use layer settings"],
    [
      step(
        ["tutorial.layer_settings.step1.title", "Open Fish Evidence settings"],
        richCopy(
          [
            "tutorial.layer_settings.step1.body",
            "In {$layers}, click the {$fish_evidence} row to expand its settings. If it started expanded, close and reopen it once.",
          ],
          { layers: LAYERS_ICON, fish_evidence: FISH_EVIDENCE_ICON },
        ),
        "cycle-fish-evidence-settings",
        {
          resumeBody: richCopy(
            [
              "tutorial.layer_settings.step1.resume",
              "{$fish_evidence} settings are closed. Open the row again to finish the cycle.",
            ],
            { fish_evidence: FISH_EVIDENCE_ICON },
          ),
        },
      ),
      step(
        ["tutorial.layer_settings.step2.title", "Change layer opacity"],
        richCopy(
          [
            "tutorial.layer_settings.step2.body",
            "Move the {$fish_evidence} opacity slider and watch the evidence overlay change without hiding the layer.",
          ],
          { fish_evidence: FISH_EVIDENCE_ICON },
        ),
        "change-fish-evidence-opacity",
      ),
      step(
        ["tutorial.layer_settings.step3.title", "Close the settings"],
        richCopy(
          [
            "tutorial.layer_settings.step3.body",
            "Click the {$fish_evidence} row again to collapse its settings when you are finished.",
          ],
          { fish_evidence: FISH_EVIDENCE_ICON },
        ),
        "close-fish-evidence-settings",
      ),
    ],
  ),
  "map-presets": tutorial(
    "intermediate",
    ["tutorial.map_presets.title", "Create a map preset"],
    [
      step(
        ["tutorial.map_presets.step1.title", "Open Map Presets"],
        richCopy([
          "tutorial.map_presets.step1.body",
          "Open Settings, then choose Map Presets. Presets keep a reusable copy of your map layers, query, view, and window arrangement.",
        ]),
        "open-map-presets",
      ),
      step(
        ["tutorial.map_presets.step2.title", "Clone the current setup"],
        richCopy([
          "tutorial.map_presets.step2.body",
          "Choose Clone to create your own saved preset from the currently selected setup.",
        ]),
        "clone-map-preset",
      ),
      step(
        ["tutorial.map_presets.step3.title", "Give the preset a useful name"],
        richCopy([
          "tutorial.map_presets.step3.body",
          "Replace the cloned preset’s title with a name you will recognize, then press Enter or leave the title field to save it.",
        ]),
        "rename-map-preset",
      ),
    ],
  ),
  "catch-profile-provenance": tutorial(
    "advanced",
    ["tutorial.catch_profile_provenance.title", "Understand catch-profile provenance"],
    [
      step(
        ["tutorial.catch_profile_provenance.step1.title", "Load a catch profile"],
        richCopy(
          [
            "tutorial.catch_profile_provenance.step1.body",
            "Click open water in any fishing zone. In {$details}, use the first fish row to compare Presence and Rate evidence.",
          ],
          { details: DETAILS_ICON },
        ),
        "select-map-point",
      ),
      step(
        ["tutorial.catch_profile_provenance.step2.title", "Inspect Presence evidence"],
        richCopy(
          [
            "tutorial.catch_profile_provenance.step2.body",
            "Open the first fish's {$presence_provenance} mark. It explains why the fish belongs here, not how often it is caught. Check source, status, conditions, date, and observation count; color summarizes the evidence type.",
          ],
          { presence_provenance: PRESENCE_PROVENANCE },
        ),
        "inspect-presence-provenance",
      ),
      step(
        ["tutorial.catch_profile_provenance.step3.title", "Inspect Rate evidence"],
        richCopy(
          [
            "tutorial.catch_profile_provenance.step3.body",
            "Open the same fish's {$rate_provenance} mark. It supports Rate separately from Presence. Check value (% or weight), conditions, status, and source. Green means known, blue database-backed, and yellow uncertain.",
          ],
          { rate_provenance: RATE_PROVENANCE },
        ),
        "inspect-rate-provenance",
      ),
    ],
  ),
  "regions": tutorial(
    "advanced",
    ["tutorial.regions.title", "Use Region hover facts"],
    [
      step(
        ["tutorial.regions.step1.title", "Enable Origin"],
        richCopy(
          [
            "tutorial.regions.step1.body",
            "Open {$regions} settings, choose Hover, and turn on {$origin}. If it is already on, turn it off and on once. The fact remains available while the layer is hidden.",
          ],
          { regions: REGIONS_ICON, origin: ORIGIN_ICON },
        ),
        "enable-origin-hover-fact",
      ),
      step(
        ["tutorial.regions.step2.title", "Enable Resources"],
        richCopy(
          [
            "tutorial.regions.step2.body",
            "Open {$region_groups} settings, choose Hover, and turn on {$resources}. If it is already on, turn it off and on once. The fact remains available while the layer is hidden.",
          ],
          {
            region_groups: REGION_GROUPS_ICON,
            resources: RESOURCES_ICON,
          },
        ),
        "enable-resources-hover-fact",
      ),
      step(
        ["tutorial.regions.step3.title", "Hover a field"],
        richCopy(
          [
            "tutorial.regions.step3.body",
            "Hover a field on the map until {$origin} and {$resources} appear together. Neither the Regions nor Region Groups layer needs to be visible.",
          ],
          { origin: ORIGIN_ICON, resources: RESOURCES_ICON },
        ),
        "inspect-region-hover",
      ),
    ],
  ),
  clipping: tutorial(
    "advanced",
    ["tutorial.clipping.title", "Clip one layer through another"],
    [
      step(
        ["tutorial.clipping.step1.title", "Detach Regions from the Zone Mask"],
        richCopy(
          [
            "tutorial.clipping.step1.body",
            "In {$layers}, drag the {$regions} row to an edge of another row until an insertion line appears, then drop it. This removes its current {$zone_mask} clipping relationship.",
          ],
          {
            layers: LAYERS_ICON,
            regions: REGIONS_ICON,
            zone_mask: ZONE_MASK_ICON,
          },
        ),
        "detach-regions-clip",
        {
          detachedBody: richCopy(
            [
              "tutorial.clipping.step1.detached",
              "{$regions} is already detached. First drop it onto the center of {$zone_mask}; then drag it to a row edge and drop it again so you perform the detach operation yourself.",
            ],
            { regions: REGIONS_ICON, zone_mask: ZONE_MASK_ICON },
          ),
        },
      ),
      step(
        ["tutorial.clipping.step2.title", "Attach Regions to the Zone Mask"],
        richCopy(
          [
            "tutorial.clipping.step2.body",
            "Drag {$regions} onto the center of {$zone_mask} until the row shows the attach target, then drop it. Regions becomes indented beneath its clipping mask.",
          ],
          { regions: REGIONS_ICON, zone_mask: ZONE_MASK_ICON },
        ),
        "attach-regions-to-zone-mask",
      ),
    ],
  ),
  "advanced-queries": tutorial(
    "advanced",
    ["tutorial.advanced_queries.title", "Use container queries"],
    [
      step(
        ["tutorial.advanced_queries.step1.title", "Require one fish to match both"],
        richCopy(
          [
            "tutorial.advanced_queries.step1.body",
            "Enter {$same_fish_query}. Grade and collection status share one fish container—the same as {$explicit_same_fish_query}. A zone or hotspot matches only when one fish is both red and missing from your FishyDex.",
          ],
          {
            same_fish_query: SAME_FISH_QUERY,
            explicit_same_fish_query: EXPLICIT_SAME_FISH_QUERY,
          },
        ),
        "apply-same-fish-query",
      ),
      step(
        ["tutorial.advanced_queries.step2.title", "Frame the same-fish matches"],
        richCopy([
          "tutorial.advanced_queries.step2.body",
          "Use the target button beside Query to frame the matches. Notice which zones remain when one fish must satisfy both conditions.",
        ]),
        "focus-query",
      ),
      step(
        ["tutorial.advanced_queries.step3.title", "Allow two different fish to match"],
        richCopy(
          [
            "tutorial.advanced_queries.step3.body",
            "Replace it with {$separate_fish_query}. Each bracket creates its own fish container, so one contained fish may be red while another is missing. Both must still occur in the same zone or hotspot.",
          ],
          { separate_fish_query: SEPARATE_FISH_QUERY },
        ),
        "apply-separate-fish-query",
      ),
      step(
        ["tutorial.advanced_queries.step4.title", "Frame the separate-fish matches"],
        richCopy([
          "tutorial.advanced_queries.step4.body",
          "Use the target button again. Compare this view with the previous one: these containers may satisfy red and missing with two different fish.",
        ]),
        "focus-query",
      ),
      step(
        ["tutorial.advanced_queries.step5.title", "Inspect one shared container"],
        richCopy([
          "tutorial.advanced_queries.step5.body",
          "Select open water in a remaining zone and inspect its Catch Profile. Find the red fish and missing fish that made the zone match; they may be different fish.",
        ]),
        "select-map-point",
      ),
    ],
  ),
});

export const MAP_TUTORIAL_IDS = Object.freeze(Object.keys(MAP_TUTORIALS));
export const MAP_TUTORIAL_PROGRESS_IDS = Object.freeze(
  MAP_TUTORIAL_IDS.map((lessonId) => `map.${lessonId}`),
);

export function mapTutorialProgressId(lessonId) {
  return typeof lessonId === "string" && MAP_TUTORIALS[lessonId]
    ? `map.${lessonId}`
    : "";
}

export function normalizedTutorialStep(value, stepCount) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 0 && parsed < stepCount ? parsed : 0;
}

export function tutorialStepAction(lessonId, rawStep) {
  const tutorial = MAP_TUTORIALS[String(lessonId ?? "").trim()];
  if (!tutorial) {
    return "";
  }
  return (
    tutorial.steps[normalizedTutorialStep(rawStep, tutorial.steps.length)]?.action || ""
  );
}

export function selectionTargetsFishingArea(selection) {
  const pointKind = String(selection?.pointKind ?? "").trim().toLowerCase();
  const elementKind = String(
    selection?.detailsTarget?.elementKind ?? "",
  ).trim().toLowerCase();
  return pointKind === "clicked" && (!elementKind || elementKind === "point");
}

export function selectionTargetsHotspot(selection) {
  return String(
    selection?.detailsTarget?.elementKind ?? "",
  ).trim().toLowerCase() === "hotspot";
}

export function hoverHasRegionFacts(hover) {
  const layerIds = new Set(
    Array.isArray(hover?.layerSamples)
      ? hover.layerSamples.map((sample) => String(sample?.layerId || "").trim())
      : [],
  );
  return layerIds.has("regions") && layerIds.has("region_groups");
}

function normalizedQueryText(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function isSingleFishIdentityQuery(expression, itemId) {
  const children = Array.isArray(expression?.children) ? expression.children : [];
  const term = children[0]?.term;
  let termIdentity = null;
  if (String(term?.kind || "") === "fish") {
    termIdentity = term?.fishId;
  } else if (String(term?.kind || "") === "item") {
    termIdentity = term?.itemId;
  }
  return (
    String(expression?.type || "") === "group" &&
    expression?.negated !== true &&
    children.length === 1 &&
    String(children[0]?.type || "") === "term" &&
    children[0]?.negated !== true &&
    Number(termIdentity) === Number(itemId)
  );
}

function isSearchTerm(node, kind, valueKey, value) {
  return (
    String(node?.type || "") === "term" &&
    String(node?.term?.kind || "") === kind &&
    String(node?.term?.[valueKey] || "") === value &&
    node?.negated !== true
  );
}

function isRedTerm(node) {
  return isSearchTerm(node, "grade", "grade", "red");
}

function isMissingTerm(node) {
  return isSearchTerm(node, "status", "status", "missing");
}

function isSameFishQuery(expression, queryText) {
  const children = Array.isArray(expression?.children) ? expression.children : [];
  return (
    normalizedQueryText(queryText) === "red and missing" &&
    String(expression?.type || "") === "group" &&
    String(expression?.operator || "") === "and" &&
    children.length === 2 &&
    isRedTerm(children[0]) &&
    isMissingTerm(children[1])
  );
}

function isSeparateFishQuery(expression, queryText) {
  const children = Array.isArray(expression?.children) ? expression.children : [];
  return (
    normalizedQueryText(queryText) === "[red] and [missing]" &&
    String(expression?.type || "") === "group" &&
    String(expression?.operator || "") === "and" &&
    children.length === 2 &&
    String(children[0]?.type || "") === "container" &&
    String(children[1]?.type || "") === "container" &&
    children[0]?.negated !== true &&
    children[1]?.negated !== true &&
    isRedTerm(children[0]?.expression) &&
    isMissingTerm(children[1]?.expression)
  );
}

export function reduceTutorialInteraction(action, progress = {}, observation = {}) {
  const next = { ...progress };
  let complete = false;

  switch (String(action ?? "")) {
    case "apply-pink-dolphin":
      complete =
        observation.kind === "query-state-changed" &&
        isSingleFishIdentityQuery(observation.expression, PINK_DOLPHIN.itemId);
      break;
    case "confirm-pink-dolphin":
      complete =
        observation.kind === "tutorial-proceeded" ||
        (observation.kind === "query-state-changed" &&
          isSingleFishIdentityQuery(
            observation.expression,
            PINK_DOLPHIN.itemId,
          ) &&
          normalizedQueryText(observation.queryText) ===
            normalizedQueryText(PINK_DOLPHIN.label));
      break;
    case "apply-sea-bunny":
      complete =
        observation.kind === "query-state-changed" &&
        isSingleFishIdentityQuery(observation.expression, SEA_BUNNY.itemId);
      break;
    case "apply-same-fish-query":
      complete =
        observation.kind === "query-state-changed" &&
        isSameFishQuery(observation.expression, observation.queryText);
      break;
    case "apply-separate-fish-query":
      complete =
        observation.kind === "query-state-changed" &&
        isSeparateFishQuery(observation.expression, observation.queryText);
      break;
    case "clear-query":
      complete =
        observation.kind === "query-state-changed" &&
        Array.isArray(observation.expression?.children) &&
        observation.expression.children.length === 0;
      break;
    case "focus-query":
      complete = observation.kind === "query-focused";
      break;
    case "select-map-point":
      complete = observation.kind === "catch-profile-ready";
      break;
    case "inspect-catch-group":
    case "inspect-catch-item":
    case "inspect-catch-rate":
      complete = observation.kind === "tutorial-proceeded";
      break;
    case "inspect-presence-provenance":
      complete =
        observation.kind === "provenance-opened" &&
        observation.channel === "presence" &&
        observation.firstFish === true;
      break;
    case "inspect-rate-provenance":
      complete =
        observation.kind === "provenance-opened" &&
        observation.channel === "rate" &&
        observation.firstFish === true;
      break;
    case "cycle-query-window":
      if (
        observation.kind === "window-state-changed" &&
        observation.windowId === "search"
      ) {
        if (next.windowCyclePhase === "needs-initial-open" && observation.open) {
          next.windowCyclePhase = "needs-close";
        } else if (next.windowCyclePhase === "needs-close" && !observation.open) {
          next.windowCyclePhase = "needs-reopen";
        } else if (next.windowCyclePhase === "needs-reopen" && observation.open) {
          complete = true;
        }
      }
      break;
    case "cycle-layer-collapse":
      if (
        observation.kind === "window-state-changed" &&
        observation.windowId === "layers" &&
        observation.open
      ) {
        if (
          next.collapseCyclePhase === "needs-initial-expand" &&
          !observation.collapsed
        ) {
          next.collapseCyclePhase = "needs-collapse";
        } else if (
          next.collapseCyclePhase === "needs-collapse" &&
          observation.collapsed
        ) {
          next.collapseCyclePhase = "needs-expand";
        } else if (
          next.collapseCyclePhase === "needs-expand" &&
          !observation.collapsed
        ) {
          complete = true;
        }
      }
      break;
    case "move-layer-window":
      complete =
        observation.kind === "window-state-changed" &&
        observation.windowId === "layers" &&
        Number.isFinite(Number(observation.x)) &&
        Number.isFinite(Number(observation.y)) &&
        (
          observation.x !== next.initialWindowX ||
          observation.y !== next.initialWindowY
        );
      break;
    case "show-hotspots-layer":
      if (observation.kind === "query-state-changed") {
        next.queryActive =
          Array.isArray(observation.expression?.children) &&
          observation.expression.children.length > 0;
      }
      if (
        observation.kind === "layer-visibility-changed" &&
        observation.layerId === "hotspots"
      ) {
        next.hotspotsLayerVisible = observation.visible === true;
        next.hotspotsHidden = observation.visible !== true;
      }
      if (observation.kind === "hotspot-visibility-changed") {
        next.hotspotTotalCount = Math.max(0, Number(observation.totalCount) || 0);
        next.hotspotVisibleCount = Math.max(0, Number(observation.visibleCount) || 0);
        next.hotspotsLayerVisible = observation.layerVisible === true;
        next.hotspotsHidden = observation.layerVisible !== true;
        next.queryActive = observation.queryActive === true;
        complete = next.hotspotVisibleCount > 0;
      }
      next.hotspotsFilteredByQuery =
        next.hotspotsLayerVisible === true &&
        next.queryActive === true &&
        next.hotspotTotalCount > 0 &&
        next.hotspotVisibleCount === 0;
      break;
    case "inspect-hotspot":
      complete = observation.kind === "hotspot-selected";
      break;
    case "select-hotspot-tab":
      complete = observation.kind === "tutorial-proceeded";
      break;
    case "hide-hotspots-layer":
      complete =
        observation.kind === "layer-visibility-changed" &&
        observation.layerId === "hotspots" &&
        observation.visible === false;
      break;
    case "cycle-fish-evidence-settings":
      if (
        observation.kind === "layer-settings-changed" &&
        observation.layerId === "fish_evidence"
      ) {
        if (!observation.expanded) {
          next.fishEvidenceSettingsClosed = true;
        }
        complete =
          observation.expanded === true &&
          (
            next.fishEvidenceSettingsInitiallyExpanded !== true ||
            next.fishEvidenceSettingsClosed === true
          );
      }
      break;
    case "change-fish-evidence-opacity":
      complete =
        observation.kind === "layer-opacity-changed" &&
        observation.layerId === "fish_evidence" &&
        Number.isFinite(Number(observation.opacity)) &&
        Number(observation.opacity) !== Number(next.initialLayerOpacity);
      break;
    case "close-fish-evidence-settings":
      complete =
        observation.kind === "layer-settings-changed" &&
        observation.layerId === "fish_evidence" &&
        observation.expanded === false;
      break;
    case "place-bookmark":
      if (observation.kind === "bookmark-count-changed") {
        const previousIds = new Set(
          Array.isArray(next.bookmarkIds) ? next.bookmarkIds.map(String) : [],
        );
        const currentIds = Array.isArray(observation.bookmarkIds)
          ? observation.bookmarkIds.map(String)
          : [];
        next.createdBookmarkId =
          currentIds.find((bookmarkId) => !previousIds.has(bookmarkId)) || "";
        next.bookmarkIds = currentIds;
      }
      complete =
        observation.kind === "bookmark-count-changed" &&
        Number(observation.count) > Number(next.bookmarkCount || 0) &&
        Boolean(next.createdBookmarkId);
      break;
    case "activate-bookmark":
      complete =
        observation.kind === "bookmark-activated" &&
        Boolean(next.createdBookmarkId) &&
        observation.bookmarkId === next.createdBookmarkId;
      break;
    case "copy-details-bookmark":
      complete = observation.kind === "details-bookmark-copied";
      break;
    case "export-bookmarks":
      if (
        observation.kind === "bookmark-selection-changed" &&
        Number(observation.count) > 0
      ) {
        next.bookmarkSelected = true;
      }
      complete =
        observation.kind === "bookmark-exported" &&
        next.bookmarkSelected === true;
      break;
    case "import-bookmarks":
      complete = observation.kind === "bookmark-import-opened";
      break;
    case "open-map-presets":
      complete = observation.kind === "map-presets-opened";
      break;
    case "clone-map-preset":
      complete =
        observation.kind === "preset-changed" &&
        observation.reason === "create-preset" &&
        observation.collectionKey === "map-presets";
      break;
    case "rename-map-preset":
      complete =
        observation.kind === "preset-changed" &&
        observation.reason === "update-preset" &&
        observation.collectionKey === "map-presets";
      break;
    case "enable-origin-hover-fact":
    case "enable-resources-hover-fact": {
      const expectedLayerId = action === "enable-origin-hover-fact"
        ? "regions"
        : "region_groups";
      const expectedFactKey = action === "enable-origin-hover-fact"
        ? "origin_region"
        : "resource_group";
      if (
        observation.kind === "layer-hover-fact-changed" &&
        observation.layerId === expectedLayerId &&
        observation.factKey === expectedFactKey
      ) {
        if (observation.visible === false) {
          next.hoverFactDisabled = true;
        }
        complete =
          observation.visible === true &&
          (
            next.hoverFactInitiallyEnabled !== true ||
            next.hoverFactDisabled === true
          );
      }
      break;
    }
    case "inspect-region-hover":
      complete = observation.kind === "region-hovered";
      break;
    case "detach-regions-clip":
      if (
        observation.kind === "layer-clip-changed" &&
        observation.layerId === "regions"
      ) {
        if (observation.maskLayerId === "zone_mask") {
          next.regionsClipAttached = true;
        }
        complete =
          !observation.maskLayerId &&
          (
            next.regionsClipInitiallyDetached !== true ||
            next.regionsClipAttached === true
          );
      }
      break;
    case "attach-regions-to-zone-mask":
      complete =
        observation.kind === "layer-clip-changed" &&
        observation.layerId === "regions" &&
        observation.maskLayerId === "zone_mask";
      break;
    default:
      break;
  }

  return { progress: next, complete };
}
